import type { Clip, ClipFormat, ExportOptions, ExportRecord, PostCopy } from './clipFactoryApi'

/** The server's key for a suggested clip: its original range, before any trim. */
export const clipKey = (clip: Pick<Clip, 'start' | 'end'>) => `${clip.start}-${clip.end}`

/** 83.4 → "1:23"; an hour or more → "1:02:03". */
export const formatTime = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${String(m).padStart(2, '0')}:${s}`
}

// Clip Factory exports clips of 1 to 180 seconds.
export const MIN_CLIP_SECONDS = 1
export const MAX_CLIP_SECONDS = 180

/** A section cut out of a clip, in seconds of the video. */
/**
 * The CSS object-position x (percent) that shows a 9:16 window centred at
 * ``centre`` (0..1 of the video's width) out of a wider video, the way the
 * export crops: the window is clamped inside the frame. 50 for a video that
 * isn't wider than 9:16.
 */
export const cropObjectX = (centre: number, videoWidth: number, videoHeight: number): number => {
  const share = (9 / 16) / (videoWidth / videoHeight) // the window's share of the width
  if (!(share < 1)) return 50
  const left = Math.min(Math.max(centre - share / 2, 0), 1 - share)
  return (left / (1 - share)) * 100
}

/** The crop centre at ``time`` on a path of [time, centre] points (held until the next one). */
export const cropCentreAt = (path: [number, number][], time: number): number => {
  let centre = path[0]?.[1] ?? 0.5
  for (const [t, c] of path) {
    if (t > time) break
    centre = c
  }
  return centre
}

export interface Cut {
  start: number
  end: number
}

export interface Range {
  start: number
  end: number
  /** Sections inside the range that are left out (see mergeCuts). */
  cuts?: Cut[]
}

/** A timed word of the transcript; filler marks "um", "uh" and the like. */
export interface Word {
  start: number
  end: number
  word: string
  filler: boolean
}

const round3 = (value: number) => Math.round(value * 1000) / 1000
const MIN_CUT_SECONDS = 0.1

/** Cuts inside the range, sorted and merged, short ones dropped: as Clip Factory keeps them. */
export const mergeCuts = (range: Pick<Range, 'start' | 'end'>, cuts: Cut[]): Cut[] => {
  const spans = cuts
    .map((c) => ({ start: round3(Math.max(c.start, range.start)), end: round3(Math.min(c.end, range.end)) }))
    .filter((c) => c.end - c.start >= MIN_CUT_SECONDS)
    .sort((a, b) => a.start - b.start)
  const merged: Cut[] = []
  for (const c of spans) {
    const last = merged[merged.length - 1]
    if (last && c.start <= last.end + 0.01) last.end = Math.max(last.end, c.end)
    else merged.push({ ...c })
  }
  return merged
}

/** How long the clip runs once its cuts are left out. */
export const keptSeconds = (range: Range): number =>
  range.end - range.start - (range.cuts || []).reduce((sum, c) => sum + (c.end - c.start), 0)

// A trim handle within this many seconds of a word's edge snaps to it.
export const SNAP_SECONDS = 0.6

/** t moved to the nearest word edge (a start edge to a word's start, an end edge to its end), if one is close. */
export const snapToWord = (t: number, words: Word[], edge: 'start' | 'end'): number => {
  let best = t
  let gap = SNAP_SECONDS
  for (const w of words) {
    const at = edge === 'start' ? w.start : w.end
    if (Math.abs(at - t) < gap) {
      gap = Math.abs(at - t)
      best = at
    }
  }
  return best
}

/** The cut that removes words[i..j] and the pause after them (up to the next word). */
export const cutForWords = (words: Word[], i: number, j: number): Cut => ({
  start: words[i].start,
  end: j + 1 < words.length ? words[j + 1].start : words[j].end,
})

/** One cut per filler word ("um", "uh") that isn't cut already. */
export const fillerCuts = (words: Word[], cuts: Cut[]): Cut[] =>
  words
    .map((w, i) => ({ w, i }))
    .filter(({ w }) => w.filler && !isCut(w, cuts))
    .map(({ i }) => cutForWords(words, i, i))

/** Seconds of the clip played by video time t: the time since its start, less the cut parts before t. */
export const keptElapsed = (t: number, range: Range): number =>
  t - range.start - (range.cuts || []).reduce((sum, c) => sum + Math.max(0, Math.min(t, c.end) - c.start), 0)

/** Whether a word falls in one of the cuts (by its middle, as Clip Factory decides). */
export const isCut = (w: Word, cuts: Cut[]): boolean => {
  const mid = (w.start + w.end) / 2
  return cuts.some((c) => mid >= c.start && mid <= c.end)
}

const round = (value: number) => Math.round(value * 100) / 100

/**
 * `range` with the edge that moved kept inside the video (0 to `duration`)
 * and 1–180 s from the other edge, which stays where it was.
 */
export const clampTrim = (range: Range, duration: number, moved: 'start' | 'end'): Range => {
  const min = Math.min(MIN_CLIP_SECONDS, duration)
  let out: Range
  if (moved === 'start') {
    const end = Math.min(Math.max(range.end, min), duration)
    const start = Math.min(Math.max(range.start, 0, end - MAX_CLIP_SECONDS), end - min)
    out = { start: round(start), end: round(end) }
  } else {
    const start = Math.max(Math.min(range.start, duration - min), 0)
    const end = Math.max(Math.min(range.end, duration, start + MAX_CLIP_SECONDS), start + min)
    out = { start: round(start), end: round(end) }
  }
  // Cuts that end up outside the new range are dropped.
  return range.cuts ? { ...out, cuts: mergeCuts(out, range.cuts) } : out
}

/** What "Copy caption" copies: the title ideas, the caption and the hashtags. */
export const postCopyText = (copy: PostCopy | undefined): string => {
  if (!copy) return ''
  const tags = copy.hashtags.map((tag) => (tag.startsWith('#') ? tag : `#${tag}`)).join(' ')
  return [copy.titles.join('\n'), copy.caption, tags].filter((part) => part.trim()).join('\n\n')
}

export const formatBytes = (bytes: number): string =>
  bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(2)} GB` : `${(bytes / 1024 ** 2).toFixed(1)} MB`

/**
 * Rough processing time for a video `durationSeconds` long: transcription on
 * Clip Factory's CPU runs at about a third of real time, plus a fixed half
 * minute to load the model and pick clips. Shown as an estimate.
 */
export const estimateProcessingSeconds = (durationSeconds: number): number =>
  Math.round(Math.max(0, durationSeconds) * 0.35 + 30)

/** "about 4 min" / "under a minute", for estimateProcessingSeconds. */
export const formatEstimate = (seconds: number): string =>
  seconds < 60 ? 'under a minute' : `about ${Math.round(seconds / 60)} min`

export const wordCount = (text: string): number => text.split(/\s+/).filter(Boolean).length

// Clip Factory's limit for a "Create a video" script.
export const SCRIPT_MAX_WORDS = 120

export interface Preset {
  id: string
  label: string
  format: ClipFormat
  captions: boolean
  hook: boolean
  watermark: boolean
}

export const PRESETS: Preset[] = [
  { id: 'shorts', label: 'TikTok/Reels/Shorts', format: 'vertical_speaker', captions: true, hook: true, watermark: true },
  { id: 'feed', label: 'X/LinkedIn', format: 'original', captions: true, hook: false, watermark: true },
]

/** The settings an export is made with, as one comparable string. */
export const exportSig = (range: Range, options: ExportOptions): string =>
  JSON.stringify([range.start, range.end, options.format, options.captions, options.watermark, options.intro_outro,
    options.hook || null, options.receipt_call_id ?? null,
    range.cuts && range.cuts.length ? range.cuts.map((c) => [c.start, c.end]) : null,
    // A style only matters with captions on; absent is classic.
    options.captions ? options.caption_style || 'classic' : null,
    !!options.chart])

/** exportSig for a saved export record; records from before the branding options count as all off. */
export const recordSig = (record: ExportRecord): string =>
  exportSig({ start: record.start, end: record.end, cuts: record.cuts || undefined }, {
    format: record.format,
    captions: record.captions,
    watermark: !!record.watermark,
    intro_outro: !!record.intro_outro,
    hook: record.hook || null,
    receipt_call_id: record.receipt_call_id ?? null,
    caption_style: record.caption_style,
    // A record asked for charts if it has them or says why one was left out.
    chart: !!(record.charts?.length || record.chart_note),
  })
